import { useCallback, useEffect, useState } from 'react';
import {
  getShip,
  postShipAction,
  postShipListen,
  postShipSeen,
  type ShipAction,
  type ShipPieceIdApi,
  type ShipState,
} from '../../../lib/englishApi.js';
import { createSessionCache } from '../../../lib/sessionCache.js';

// Caché de sesión (stale-while-revalidate, como `useEnglishLevel`): volver al Inicio monta con el
// último estado al instante y el fetch de fondo trae las piezas ganadas mientras tanto, que
// entran en espiral. Se limpia al cambiar de usuario (AuthProvider → clearSessionCaches).
const shipCache = createSessionCache<ShipState>();
// Todas las instancias ven el mismo estado (la barra, el reproductor, la hoja de fin de capítulo).
const listeners = new Set<(s: ShipState) => void>();
function publish(s: ShipState): void {
  shipCache.set(s);
  listeners.forEach((l) => l(s));
}

/** El panel de debug cambió el recorrido (pestaña Nave): el Inicio lo ve al instante. */
export const shareShip = (s: ShipState): void => publish(s);

/** Lo último que se sabe del recorrido, sin pedirlo. */
export const peekShip = (): ShipState | null => shipCache.get();

/** Pide el recorrido una vez si todavía no se sabe (p.ej. se entró directo a la Audioteca). */
let loading: Promise<ShipState | null> | null = null;
export function ensureShip(): Promise<ShipState | null> {
  const cached = shipCache.get();
  if (cached) return Promise.resolve(cached);
  loading ??= getShip()
    .then((s) => {
      publish(s);
      return s;
    })
    .catch((err: unknown) => {
      console.warn('[ship] no se pudo leer el recorrido', err);
      return null;
    })
    .finally(() => {
      loading = null;
    });
  return loading;
}

/** La Antena: suma segundos escuchados de verdad (ver `shipListenMeter.ts`). */
export async function reportListen(seconds: number, chapterDone = false): Promise<void> {
  publish(await postShipListen(seconds, chapterDone));
}

export interface UseShip {
  /** `null` mientras carga la primera vez (o si falló sin caché). */
  ship: ShipState | null;
  /** Ya hay una respuesta (o un error): el Inicio puede revelarse. */
  resolved: boolean;
  act: (action: ShipAction) => Promise<ShipState>;
  markSeen: (pieces: readonly ShipPieceIdApi[]) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useShip(): UseShip {
  const cached = shipCache.get();
  const [ship, setShip] = useState<ShipState | null>(cached);
  const [resolved, setResolved] = useState(cached !== null);

  useEffect(() => {
    listeners.add(setShip);
    return () => {
      listeners.delete(setShip);
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      publish(await getShip());
    } catch (err) {
      // El recorrido no es crítico: si /ship no responde, el Inicio se muestra el de siempre.
      console.warn('[ship] no se pudo leer el recorrido', err);
    } finally {
      setResolved(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const act = useCallback(async (action: ShipAction) => {
    const next = await postShipAction(action);
    publish(next);
    return next;
  }, []);

  const markSeen = useCallback(async (pieces: readonly ShipPieceIdApi[]) => {
    if (pieces.length === 0) return;
    publish(await postShipSeen(pieces));
  }, []);

  return { ship, resolved, act, markSeen, refresh };
}

/**
 * Lo mandado con «Inspirar» no cuenta para la Cabina desde A1 (Emmanuel, 22/10, card #603): en esa
 * charla, «Usar esto» deja la frase a la vista para leerla en voz alta en vez de mandarla. Sólo con
 * el recorrido en curso y la Cabina por hacer; lee el estado que ya pidió la barra de la pieza.
 */
export function useInspireReadsAloud(piece: ShipPieceIdApi | null): boolean {
  const [ship, setShip] = useState<ShipState | null>(() => shipCache.get());
  useEffect(() => {
    listeners.add(setShip);
    return () => {
      listeners.delete(setShip);
    };
  }, []);
  if (piece !== 'cabina' || ship?.status !== 'active') return false;
  const cabina = ship.pieces.find((p) => p.id === 'cabina');
  return cabina !== undefined && !cabina.done && cabina.said?.inspireCounts === false;
}
